import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DataTable from '../../components/ui/data-table';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import { 
  Search, 
  UserPlus, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Users,
  FileText,
  AlertCircle
} from 'lucide-react';

const ManageChildren = () => {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [requests, setRequests] = React.useState([]);
  const [activeTab, setActiveTab] = React.useState('search');

  // Form state
  const [selectedStudent, setSelectedStudent] = React.useState(null);
  const [relationship, setRelationship] = React.useState('guardian');
  const [isPrimary, setIsPrimary] = React.useState(false);
  const [canPickup, setCanPickup] = React.useState(true);
  const [emergencyContact, setEmergencyContact] = React.useState(false);
  const [financialResponsible, setFinancialResponsible] = React.useState(false);
  const [academicAccess, setAcademicAccess] = React.useState(true);
  const [notes, setNotes] = React.useState('');

  // Search for students
  const handleSearch = async () => {
    if (!searchTerm || searchTerm.length < 2) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/v1/users/search-students?q=${encodeURIComponent(searchTerm)}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const responseData = await response.json();
        setSearchResults(responseData.data.students || []);
      } else {
        console.error('Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Submit link request
  const handleSubmitRequest = async () => {
    if (!selectedStudent) return;

    setSubmitting(true);
    try {
      const response = await fetch('/api/v1/users/parent-student-requests', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId: selectedStudent.id,
          relationship,
          isPrimary,
          canPickup,
          emergencyContact,
          financialResponsible,
          academicAccess,
          notes
        })
      });

      if (response.ok) {
        await response.json();
        alert('Link request submitted successfully!');
        // Reset form
        setSelectedStudent(null);
        setSearchTerm('');
        setSearchResults([]);
        setNotes('');
        // Refresh requests
        fetchRequests();
        setActiveTab('requests');
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch user's requests
  const fetchRequests = async () => {
    try {
      const response = await fetch('/api/v1/users/parent-student-requests', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data.data.requests || []);
      }
    } catch (error) {
      console.error('Fetch requests error:', error);
    }
  };

  React.useEffect(() => {
    fetchRequests();
  }, []);


  const requestColumns = [
    { id: 'student', header: 'Student', accessorKey: 'student.firstName', sortable: true },
    { id: 'relationship', header: 'Relationship', accessorKey: 'relationship', sortable: true },
    { id: 'status', header: 'Status', accessorKey: 'status', sortable: true },
    { id: 'createdAt', header: 'Requested', accessorKey: 'createdAt', sortable: true },
    { id: 'reviewedAt', header: 'Reviewed', accessorKey: 'reviewedAt', sortable: true }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Children</h1>
          <p className="text-gray-600">Link your account to your children's profiles</p>
        </div>
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-600">Parent Portal</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="search">Search & Link</TabsTrigger>
          <TabsTrigger value="requests">My Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Search className="w-5 h-5 mr-2" />
                Search for Student
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <div className="flex-1">
                  <Input
                    placeholder="Search by name, admission number, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <Button onClick={handleSearch} disabled={loading || !searchTerm}>
                  {loading ? <LoadingSpinner /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium">Search Results</h3>
                  {searchResults.map((student) => (
                    <div
                      key={student.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedStudent?.id === student.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedStudent(student)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{student.firstName} {student.lastName}</h4>
                          <p className="text-sm text-gray-600">
                            {student.admissionNumber} • {student.email}
                          </p>
                        </div>
                        {selectedStudent?.id === student.id && (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {selectedStudent && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Link Request Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900">
                    {selectedStudent.firstName} {selectedStudent.lastName}
                  </h4>
                  <p className="text-sm text-blue-700">
                    {selectedStudent.admissionNumber} • {selectedStudent.email}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="relationship">Relationship</Label>
                    <Select value={relationship} onValueChange={setRelationship}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="father">Father</SelectItem>
                        <SelectItem value="mother">Mother</SelectItem>
                        <SelectItem value="guardian">Guardian</SelectItem>
                        <SelectItem value="uncle">Uncle</SelectItem>
                        <SelectItem value="aunt">Aunt</SelectItem>
                        <SelectItem value="grandparent">Grandparent</SelectItem>
                        <SelectItem value="sibling">Sibling</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Additional information about the relationship..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium">Permissions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="isPrimary"
                        checked={isPrimary}
                        onCheckedChange={setIsPrimary}
                      />
                      <Label htmlFor="isPrimary">Primary Parent/Guardian</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="canPickup"
                        checked={canPickup}
                        onCheckedChange={setCanPickup}
                      />
                      <Label htmlFor="canPickup">Can Pick Up Student</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="emergencyContact"
                        checked={emergencyContact}
                        onCheckedChange={setEmergencyContact}
                      />
                      <Label htmlFor="emergencyContact">Emergency Contact</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="financialResponsible"
                        checked={financialResponsible}
                        onCheckedChange={setFinancialResponsible}
                      />
                      <Label htmlFor="financialResponsible">Financial Responsibility</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="academicAccess"
                        checked={academicAccess}
                        onCheckedChange={setAcademicAccess}
                      />
                      <Label htmlFor="academicAccess">Academic Records Access</Label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setSelectedStudent(null)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSubmitRequest} disabled={submitting}>
                    {submitting ? <LoadingSpinner /> : 'Submit Request'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="requests" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                My Link Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {requests.length > 0 ? (
                <DataTable
                  data={requests.map(request => ({
                    ...request,
                    student: `${request.student?.firstName} ${request.student?.lastName}`,
                    createdAt: new Date(request.createdAt).toLocaleDateString(),
                    reviewedAt: request.reviewedAt ? new Date(request.reviewedAt).toLocaleDateString() : 'Pending'
                  }))}
                  columns={requestColumns}
                  searchKey="student"
                />
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Requests Yet</h3>
                  <p className="text-gray-600">You haven't submitted any link requests yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManageChildren;
